import { Component, OnInit, OnDestroy, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormControl, Validators, AbstractControl } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';
import { Toast } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { KeyFilterModule } from 'primeng/keyfilter';
import { debounceTime, distinctUntilChanged, filter, map, Subject, takeUntil } from 'rxjs';
import { FilingFormStateService } from '../../../services/mbrs/filing-form-state.service';
import { FormSchema, FormControlSchema } from '../../../models/form-schema.model';
import { FieldWarningService } from '../../../services/mbrs/field-warning.service';
import { WarningResult } from '../../../models/validation-warning.model';
import { EnvironmentService } from '../../../services/common/environment.service';
import { SubmitForValidationComponent } from "../../common/submit-for-validation/submit-for-validation.component";
import { RichTextFieldComponent } from '../../common/rich-text-field/rich-text-field.component';
import { WarningMessageComponent, WarningMessage } from "../../common/warning-message/warning-message.component";
import { handleDateInputChange, getFieldMaxLength } from '../../../utils/helpers';
import {
  FORM_AUTOSAVE_DEBOUNCE_MS,
  WARNING_EVALUATION_DEBOUNCE_MS,
} from '../../../utils/debounce-times.util';
import {
  ROLE5_NUMBER_OF_DIRECTORS_KEY,
  evaluateRole5OtherPersonRequired,
  getRole5VisibleDirectorCount,
  role5TypeRequiresPattern,
} from '../../../services/mbrs/validators/role5-statement-by-directors.rules';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import {
  canonicalizeIdentificationTypes,
  FREE_TEXT_IDENTIFICATION_MAX_LENGTH,
  NUMERIC_IDENTIFICATION_MAX_LENGTH,
} from '../../../utils/identification-field.util';
import { buildFormEditPipeline } from '../../../utils/form-edit-pipeline.util';
import {
  TOAST_DURATION_DEFAULT_MS,
  TOAST_DURATION_LONG_MS,
} from '../../../utils/toast-durations.util';
import { PlaceholderPipe, FixedPlaceholderPipe } from '../../../utils/pipes/placeholder.pipe';
import { SelectClearIconBehaviorDirective } from '../../../directives/select-clear-icon-behavior.directive';
import { RequiredMarkerComponent } from '../../common/required-marker/required-marker.component';
import { isControlMandatory } from '../../../utils/required-field.util';

import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import {
  computedControlPredicate,
  revealIfInvalid,
  revealValidationErrors,
  syncRevealToValidity,
} from '../../../utils/validation-reveal.util';
import { adoptIntoAutoSaveSheet } from '../../../utils/adopt-remote-sheet-state.util';
import {
  LoadTrace,
  NO_OP_LOAD_TRACE,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from '../../../utils/sheet-load-trace.util';
import {
  classifySheetInitError,
  fetchJsonAsset,
  fetchSheetSchema,
} from '../../../utils/schema-asset.util';
@Component({
  selector: 'app-role5-120100-statement-by-directors',
  standalone: true,
  imports: [
    SheetLoaderComponent,
    RequiredMarkerComponent,
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputTextModule,
    Select,
    SelectClearIconBehaviorDirective,
    ButtonModule,
    DatePicker,
    TooltipModule,
    Toast,
    KeyFilterModule,
    SubmitForValidationComponent,
    RichTextFieldComponent,
    WarningMessageComponent,
    PlaceholderPipe,
    FixedPlaceholderPipe
  ],
  providers: [MessageService],
  templateUrl: './role5-120100-statement-by-directors.component.html',
  styleUrls: ['./role5-120100-statement-by-directors.component.css']
})
export class Role5120100StatementByDirectorsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private destroyRef = inject(DestroyRef);
  private stateService = inject(FilingFormStateService);
  private messageService = inject(MessageService);
  private loadTrace = inject(SheetLoadTraceService);
  /**
   * This mount's load trace. Opened as the first act of `ngOnInit` and closed
   * either by the terminal step mark or by the init catch, so a failure reports
   * which of the seven load steps this sheet actually reached.
   *
   * @see src/app/utils/sheet-load-trace.util.ts
   */
  private loadTraceHandle: LoadTrace = NO_OP_LOAD_TRACE;
  private warningService = inject(FieldWarningService);
  private envService = inject(EnvironmentService);

  /** Check if the current environment is development (for showing debug info like role IDs) */
  get isDev(): boolean {
    return this.envService.isDevelopment();
  }

  statementByDirectorsForm!: FormGroup;
  schema!: FormSchema;
  typeDict: any = {};
  isLoading = true;

  // Dropdown options
  dropdownOptions: { [key: string]: any[] } = {};

  // Flag to track if we're loading saved state (to prevent auto-save during load)
  private isLoadingState = false;

  /**
   * Set once this sheet's initial load has settled. Past that point
   * `setupStateListener` stops re-patching the form — see its docblock for why
   * a post-init repaint blanks a date the user is half-way through typing.
   * Mirrors role23/role24, which carry the identical listener and guard.
   */
  private isInitialLoadComplete = false;

  // Dynamic form title with Type of Submission
  /**
   * Card header carrying the filing's submission type, e.g.
   * "Statement by Directors (FS-MPERS)".
   *
   * Two rules, both of which this sheet used to break:
   *  - the name comes from the canonical `SHEET_DISPLAY_NAMES` map (via
   *    `getSheetTitleWithSubmissionType`), never a literal repeated here;
   *  - it is a LIVE getter, never an `ngOnInit` snapshot — see the cold-mount
   *    rule on `FilingFormStateService.getFormTitleWithSubmissionType()`.
   */
  get formTitle(): string {
    return this.stateService.getSheetTitleWithSubmissionType(
      SHEET_KEYS.statementByDirectors
    );
  }

  // Field warnings storage
  fieldWarnings: { [fieldName: string]: WarningResult[] } = {};

  // Track conditional field visibility based on requiredIfAllVisibleDirectorsMatch
  conditionalFieldsVisibility: { [qname: string]: boolean } = {};

  // Identification field mappings built dynamically from schema's conditionalOn property
  // Maps identification number field -> { typeField, values, directorNumber }
  private identificationFieldMappings: Array<{
    numberField: string;
    typeField: string;
    values: string[];
    directorNumber: number | null;
  }> = [];

  async ngOnInit(): Promise<void> {
    // Open this mount's load trace before anything else, so a failure
    // anywhere below reports which load steps were reached.
    this.loadTraceHandle = this.loadTrace.beginSheetLoad(
      '5',
      this.stateService.getConversionId(),
      this.destroyRef,
    );
    // This sheet does not await the container's MBRS hydration, so step 1
    // has nothing to wait for and is satisfied on arrival.
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.hydrationSettled);
    try {
      await this.loadSchema();
      await this.loadTypeDict();
      this.buildForm();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.formBuilt);
      this.loadSavedState();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
      this.setupStatePersistence();
      this.setupStateListener();
      this.setupWarningEvaluation(); // Setup warning evaluation for all fields
      this.setupOtherPersonFieldsManagement(); // Setup conditional visibility/validation for Other Person fields

      // Reveal the sheet's already-computed errors now that the form exists and
      // has been populated. Deterministic replacement for the filing-form
      // container's setTimeout-driven markAllAsTouched, which silently no-ops
      // when a sheet's async init has not yet assigned its FormGroup — see
      // revealValidationErrors' docblock. Touch-only: cannot move the verdict.
      revealValidationErrors(this.statementByDirectorsForm, {
        isComputed: computedControlPredicate(this.schema?.controls),
      });
      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    } catch (error) {
      // Report through the trace instead of a fixed sentence. The toast now
      // names this sheet, the steps it reached, and whether the schema was
      // missing, unreadable, or fine but the form could not be built —
      // `classifySheetInitError` infers the last of those by elimination.
      // Must run BEFORE the loading flag is cleared below: `fail()` closes the
      // trace, which is what stops the finalize mark on the next line from
      // recording this failed init as a success.
      this.loadTraceHandle.fail(classifySheetInitError(error), error);
      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Setup listener for external state changes (e.g., data loaded from backend)
   * This allows the form to react when the container populates data via the state service
   */
  private setupStateListener(): void {
    this.stateService.state$
      .pipe(
        map((state) => state.statementByDirectors),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((statementByDirectorsData) => {
        if (statementByDirectorsData && Object.keys(statementByDirectorsData).length > 0) {
          // After initial load completes, skip re-patching. `state$` is the
          // MODIFIED stream, so this sheet's own debounced persist comes
          // straight back here — and `patchValue` reaches PrimeNG's
          // `writeValue`, which re-renders the input from the model even under
          // `emitEvent: false`. While a date is half-typed that model is null
          // (`DatePicker.onUserInput` calls `updateModel(null)` on text its
          // `parseValueFromString` rejects), so the echo blanks the digits the
          // user just entered. Adopting a SIBLING TAB's values is
          // `adoptRemoteState()`'s job, on `remoteUpdateApplied$`.
          // Mirrors role12/role23/role24. See isInitialLoadComplete.
          if (this.isInitialLoadComplete) {
            return;
          }

          // Temporarily set isLoadingState to prevent auto-save during external data load
          this.isLoadingState = true;

          // Convert date string to Date object for PrimeNG DatePicker
          let processedData = { ...statementByDirectorsData };
          if (processedData['ssmt:DateOfSigningStatementByDirectors']) {
            processedData['ssmt:DateOfSigningStatementByDirectors'] = new Date(
              processedData['ssmt:DateOfSigningStatementByDirectors']
            );
          }

          // Canonicalize TypeOfIdentification values (case-insensitive snap to the enum)
          // so the dropdowns bind even when the source casing drifts (e.g. the
          // "other person" type arriving as "Police ID Number" vs "Police ID number").
          processedData = canonicalizeIdentificationTypes(
            processedData,
            this.typeDict?.enums?.['TypeOfIdentification'] ?? [],
          );

          // Patch the form with the new data
          this.statementByDirectorsForm.patchValue(processedData, { emitEvent: false });

          // IMPORTANT: Re-apply validators based on number of directors from backend data
          // This ensures only visible director fields are required. Applied
          // UNCONDITIONALLY: when the incoming count is absent/cleared the
          // resolver yields 0, and the director fields must shed `required`
          // to match the fact that none of them are rendered.
          this.updateDirectorFieldValidators(
            this.resolveDirectorCount(
              statementByDirectorsData[ROLE5_NUMBER_OF_DIRECTORS_KEY]
            )
          );

          // Re-enable auto-save after a short delay
          setTimeout(() => {
            this.isLoadingState = false;
            // Trigger warning re-evaluation after backend data is loaded
            this.evaluateWarningsManually();

            // Re-evaluate conditional fields visibility after data load
            this.updateConditionalFieldsVisibility();
          }, 100);
        }
      });
  }

  /**
   * THE single reader for "how many directors are currently selected".
   *
   * Returns `ROLE5_NO_DIRECTOR_COUNT_SELECTED` (0) whenever the dropdown is
   * cleared (`null`, `''`) or holds something unparseable, so every consumer —
   * visibility (`shouldShowField`), validators (`updateDirectorFieldValidators`,
   * `setupConditionalValidators`), the requiredIfAllVisibleDirectorsMatch
   * evaluator and the change watcher — agrees that a cleared count means ZERO
   * visible directors rather than a hardcoded default of 2.
   *
   * This replaced four copies of `value ? parseInt(value, 10) : 2`; add new
   * director-count reads here, not at the call site.
   *
   * DELEGATES to the rules module's `getRole5VisibleDirectorCount` — the same
   * function the pure validator and the submit projector call — so the parse
   * and its empty/NaN handling exist exactly once. A second local parser is how
   * the 2026-07-28 divergence (component 0, projector 2) got in.
   */
  private resolveDirectorCount(rawValue: unknown): number {
    return getRole5VisibleDirectorCount({
      [ROLE5_NUMBER_OF_DIRECTORS_KEY]: rawValue,
    });
  }

  /** Current director count straight off the form (0 when cleared). */
  private getSelectedDirectorCount(countField: string = ROLE5_NUMBER_OF_DIRECTORS_KEY): number {
    return this.resolveDirectorCount(
      this.statementByDirectorsForm?.get(countField)?.value
    );
  }

  /**
   * Load the form schema from JSON
   */
  private async loadSchema(): Promise<void> {
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaSelected);
    this.schema = await fetchSheetSchema<FormSchema>(
      this.loadTraceHandle,
      '/assets/forms/role-120100.form.json',
    );
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaParsed);
  }

  /**
   * Load type dictionary for dropdown options
   */
  private async loadTypeDict(): Promise<void> {
    // FATAL by design: without the dictionary the dropdowns have no options and
    // the conditional mappings cannot run, so the sheet must not open as if it had
    // loaded. The typed error propagates to the sheet's single catch, which
    // reports ASSET / PARSE / NET through the load trace.
    this.typeDict = await fetchJsonAsset<any>('/assets/types.dict.json');
    this.buildDropdownOptions();
  }

  /**
   * Build dropdown options from type dictionary
   */
  private buildDropdownOptions(): void {
    if (!this.typeDict.enums) return;

    Object.keys(this.typeDict.enums).forEach(key => {
      this.dropdownOptions[key] = this.typeDict.enums[key].map((value: string) => ({
        label: value,
        value: value
      }));
    });
  }

  /**
   * Build identification field mappings from schema's conditionalOn property
   * This dynamically discovers identification number fields and their corresponding type fields
   * by reading the pattern validator's conditionalOn configuration
   */
  private buildIdentificationFieldMappings(): void {
    if (!this.schema?.controls) return;

    this.identificationFieldMappings = [];

    this.schema.controls.forEach(control => {
      // Look for pattern validators with conditionalOn
      const patternValidator = control.validators?.find(validator =>
        validator.name === 'pattern' && (validator as any).conditionalOn
      );

      if (patternValidator) {
        const conditionalOn = (patternValidator as any).conditionalOn;
        if (conditionalOn?.qname && conditionalOn?.values) {
          this.identificationFieldMappings.push({
            numberField: control.qname,
            typeField: conditionalOn.qname,
            values: conditionalOn.values,
            directorNumber: control.ui.directorNumber ?? null
          });
        }
      }
    });
  }

  /**
   * Build the reactive form from schema
   */
  private buildForm(): void {
    if (!this.schema || !this.schema.controls) {
      console.error('Schema not loaded - cannot build form');
      throw new Error('Schema must be loaded before building form');
    }

    // Build identification field mappings from schema's conditionalOn property
    this.buildIdentificationFieldMappings();

    const formControls: { [key: string]: FormControl<any> } = {};

    this.schema.controls.forEach((control: FormControlSchema) => {
      const validators = [];

      // Add required validator only for non-director fields
      // Director fields (1-5) will have validators set dynamically by updateDirectorFieldValidators()
      if (control.required && !control.ui.directorNumber) {
        validators.push(Validators.required);
      }

      // The schema's declared maxLength for text controls. Director fields get
      // theirs from updateDirectorFieldValidators (which replaces the whole
      // list), and the "other person" trio from updateConditionalFieldsVisibility
      // — the same reason role 4 re-applies it in both places.
      if (!control.ui.directorNumber && !control.requiredIfAllVisibleDirectorsMatch) {
        const maxLength = getFieldMaxLength(control.qname, this.schema);
        if (maxLength !== null) {
          validators.push(Validators.maxLength(maxLength));
        }
      }

      // DO NOT add pattern validator here for identification fields
      // Pattern validators will be added conditionally by setupConditionalValidators()
      // based on the type selection (MyKad/MyPR/MyKAS)

      // Get default value
      let defaultValue = '';
      if (control.ui.defaultValue) {
        defaultValue = control.ui.defaultValue;
      }

      // Create form control
      const formControl = new FormControl(
        { value: defaultValue, disabled: control.ui.readonly || false },
        validators
      );

      formControls[control.qname] = formControl;
    });

    this.statementByDirectorsForm = new FormGroup(formControls);

    // Seat the director validators from the CURRENT count (0 on a fresh sheet,
    // where the dropdown has no selection yet), so a director field is never
    // both hidden and `required` — a blocking error the user can neither see
    // nor reach. Previously seeded from a hardcoded default of 2.
    this.updateDirectorFieldValidators(this.getSelectedDirectorCount());

    // Setup conditional validators for identification number fields after form is created
    this.setupConditionalValidators();

    // Setup auto-reset watchers for type of identification fields
    this.setupAutoResetWatchers();
  }

  /**
   * Load saved state from state service
   */
  private loadSavedState(): void {
    this.isLoadingState = true;
    this.stateService.markSheetInitializing(SHEET_KEYS.statementByDirectors);
    const savedData = this.stateService.getSheetData(SHEET_KEYS.statementByDirectors);

    if (savedData && Object.keys(savedData).length > 0) {

      // Convert date strings back to Date objects for PrimeNG DatePicker
      let processedData = { ...savedData };
      if (processedData['ssmt:DateOfSigningStatementByDirectors']) {
        processedData['ssmt:DateOfSigningStatementByDirectors'] = new Date(processedData['ssmt:DateOfSigningStatementByDirectors']);
      }

      // Canonicalize TypeOfIdentification values (case-insensitive snap to the enum)
      // so the dropdowns bind even when the persisted casing drifts. Shared with role4.
      processedData = canonicalizeIdentificationTypes(
        processedData,
        this.typeDict?.enums?.['TypeOfIdentification'] ?? [],
      );

      this.statementByDirectorsForm.patchValue(processedData, { emitEvent: false });

      // Apply validators based on loaded number of directors (0 when the saved
      // count is absent or cleared — hidden fields must not stay required).
      this.updateDirectorFieldValidators(
        this.resolveDirectorCount(savedData[ROLE5_NUMBER_OF_DIRECTORS_KEY])
      );
    }

    // Small delay to ensure form is fully initialized before enabling auto-save
    setTimeout(() => {
      this.isLoadingState = false;
      // Trigger warning evaluation after saved data is loaded
      this.evaluateWarningsManually();
      // Re-evaluate conditional fields visibility after loading saved state
      this.updateConditionalFieldsVisibility();

      // Snapshot baseline after all debounced valueChanges settle
      setTimeout(() => {
        // Re-baseline the transformed form value before snapshotting. loadSavedState
        // patched the form (emitEvent:false) AFTER canonicalizing TypeOfIdentification
        // casing and normalizing the rich-text field on load, but those transformed
        // values never reached modifiedState — so the snapshot would otherwise capture
        // the RAW shape and the first edit's persist would diff forever (pencil stuck
        // after revert). Push form.value (matching the auto-save persist) with
        // skipEditedMark=true so `original` equals what the form persists.
        this.stateService.updateSheetData(
          SHEET_KEYS.statementByDirectors,
          this.statementByDirectorsForm.value,
          this.statementByDirectorsForm.valid,
          true, // skipEditedMark — re-baseline, not a user edit
        );
        this.stateService.markSheetInitializedWithStatus(SHEET_KEYS.statementByDirectors, !this.statementByDirectorsForm.valid);
        // Initial load has settled — from here the state listener stops re-patching.
        this.isInitialLoadComplete = true;
      }, 600);
    }, 100);
  }

  /**
   * Setup auto-save on form changes
   */
  private setupStatePersistence(): void {
    buildFormEditPipeline({
      source$: this.statementByDirectorsForm.valueChanges,
      debounceMs: FORM_AUTOSAVE_DEBOUNCE_MS,
      destroyRef: this.destroyRef,
      shouldSkip: () => this.isLoadingState,
      persist: () => {
        const isValid = this.statementByDirectorsForm.valid;
        this.stateService.updateSheetData(SHEET_KEYS.statementByDirectors, this.statementByDirectorsForm.value, isValid);
      },
      evaluateStatus: () => {
        this.stateService.updateSheetLevelStatus(SHEET_KEYS.statementByDirectors, { hasErrors: !this.statementByDirectorsForm.valid });
      },
    });

    // Watch for changes in number of directors to manage field visibility
    this.setupDirectorFieldsWatcher();
  }

  /**
   * Setup watcher for number of directors field to manage dynamic field visibility
   */
  private setupDirectorFieldsWatcher(): void {
    const numberOfDirectorsControl = this.statementByDirectorsForm.get(ROLE5_NUMBER_OF_DIRECTORS_KEY);

    if (numberOfDirectorsControl) {
      numberOfDirectorsControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => {
          const selectedNumber = this.resolveDirectorCount(value);

          // Prune UNCONDITIONALLY, including on a CLEARED count (the dropdown's
          // [showClear] X, or the Delete key it enables on PrimeNG's Select),
          // which resolves to 0 and therefore blanks EVERY director block.
          //
          // The 2026-07-28 revision skipped the prune on a cleared count so the
          // values survived a clear-then-reselect round trip. That was reversed
          // 2026-07-29: it made clearing behave differently from every other
          // downward change (5 -> 2 prunes directors 3-5; 5 -> cleared pruned
          // nothing) and left hidden values sitting in the persisted state. The
          // round-trip data loss is accepted — it is identical to what already
          // happens on 5 -> 2 -> 5, and hiding a field while keeping its value
          // is the leak this prune exists to prevent.
          //
          // The three "other person" fields carry no ui.directorNumber, so they
          // are untouched here; their required-state is handled by
          // updateConditionalFieldsVisibility.
          this.clearHiddenDirectorFields(selectedNumber);

          // Update validators for director fields. ALWAYS runs, including on a
          // cleared count: the hidden director fields must shed `required` or
          // they block submission with an error the user cannot reach.
          this.updateDirectorFieldValidators(selectedNumber);
        });
    }
  }

  /**
   * Setup management of conditional fields based on requiredIfAllVisibleDirectorsMatch from schema
   * Reads configuration from JSON schema - no hardcoded qnames
   */
  private setupOtherPersonFieldsManagement(): void {
    // Find all controls with requiredIfAllVisibleDirectorsMatch configuration
    const conditionalControls = this.schema.controls.filter(control => control.requiredIfAllVisibleDirectorsMatch);

    if (conditionalControls.length === 0) return;

    // Collect all unique trigger fields (director count + director disclosure
    // fields) PLUS the trio's own three fields — under condition B of
    // evaluateRole5OtherPersonRequired, filling or emptying any one of them
    // flips the required-state of the other two, so each must re-run the
    // evaluation. Without these three the "type the ID number first" case never
    // makes Name and Type mandatory.
    const triggerFields = new Set<string>();

    conditionalControls.forEach(control => {
      const config = control.requiredIfAllVisibleDirectorsMatch!;
      triggerFields.add(config.directorCountField);
      config.directorFields.forEach(field => triggerFields.add(field));
      triggerFields.add(control.qname);
    });

    // Initial evaluation
    this.updateConditionalFieldsVisibility();

    // Watch all trigger fields for changes
    triggerFields.forEach(fieldName => {
      const formControl = this.statementByDirectorsForm.get(fieldName);
      if (formControl) {
        formControl.valueChanges
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.updateConditionalFieldsVisibility();
          });
      }
    });
  }

  /**
   * Evaluate requiredIfAllVisibleDirectorsMatch condition for a control
   * Returns true if all visible directors match the required value
   */
  private evaluateOtherPersonRequired(schemaControl: FormControlSchema): boolean {
    // DELEGATES to the rules module so the editor, the pure validator and the
    // headless listing evaluate ONE definition of "is the other-person trio
    // mandatory?". A second local copy is how the 2026-07-28 director-count
    // divergence got in; see evaluateRole5OtherPersonRequired for both
    // conditions and why condition B exists.
    //
    // Both halves come off the schema control itself — the director-match
    // config and the group membership — so nothing here hardcodes a qname.
    return evaluateRole5OtherPersonRequired(
      this.statementByDirectorsForm.getRawValue(),
      schemaControl.requiredIfAllVisibleDirectorsMatch!,
      schemaControl.requiredIfAnyOfGroupFilled ?? [],
    );
  }

  /**
   * Update validators for all conditional fields based on schema configuration
   * Fields with requiredIfAllVisibleDirectorsMatch are always visible,
   * but only required when the condition is met
   */
  private updateConditionalFieldsVisibility(): void {
    // Find all controls with requiredIfAllVisibleDirectorsMatch configuration
    const conditionalControls = this.schema.controls.filter(control => control.requiredIfAllVisibleDirectorsMatch);

    conditionalControls.forEach(schemaControl => {
      const config = schemaControl.requiredIfAllVisibleDirectorsMatch!;
      const isRequired = this.evaluateOtherPersonRequired(schemaControl);

      // Update required status tracking (used by isFieldRequired)
      this.conditionalFieldsVisibility[schemaControl.qname] = isRequired;

      // Update form control validators
      const formControl = this.statementByDirectorsForm.get(schemaControl.qname);
      if (!formControl) return;

      // The trio is always rendered, so its cap applies in BOTH branches.
      const trioMaxLength = getFieldMaxLength(schemaControl.qname, this.schema);
      const capValidators = trioMaxLength !== null ? [Validators.maxLength(trioMaxLength)] : [];

      if (isRequired) {
        // Add required validator when all visible directors are "Not primarily responsible..."
        const validators = [Validators.required, ...capValidators];

        // Check for pattern validator in schema that should be applied conditionally
        if (this.shouldUseNumericFilter(schemaControl.qname)) {
          const patternValidator = schemaControl.validators?.find(validator => validator.name === 'pattern');
          if (patternValidator && (patternValidator as any).pattern) {
            validators.push(Validators.pattern((patternValidator as any).pattern));
          }
        }

        formControl.setValidators(validators);
      } else {
        // Field is optional when condition is not met
        // Keep only non-required validators (the cap, and the conditional
        // pattern for identification fields)
        const validators = [...capValidators];

        // Keep pattern validator if applicable
        if (this.shouldUseNumericFilter(schemaControl.qname)) {
          const patternValidator = schemaControl.validators?.find(validator => validator.name === 'pattern');
          if (patternValidator && (patternValidator as any).pattern) {
            validators.push(Validators.pattern((patternValidator as any).pattern));
          }
        }

        if (validators.length > 0) {
          formControl.setValidators(validators);
        } else {
          formControl.clearValidators();
        }
        // Do NOT clear the value - field is always visible and user may have entered data
      }

      formControl.updateValueAndValidity({ emitEvent: false });

      // A field that just BECAME mandatory-and-empty must say so straight away,
      // whether it got there on page load or because a sibling in the trio was
      // filled or cleared (P.O. 2026-08-24). Reveal-only: the control that
      // triggered this re-seat is the one being typed into, so its pristine /
      // dirty state is left alone. Cannot move the sheet's verdict.
      revealIfInvalid(formControl);
    });

  }

  /**
   * Clear values for director fields that should be hidden
   */
  private clearHiddenDirectorFields(selectedNumber: number): void {
    this.schema.controls.forEach(control => {
      if (control.ui.directorNumber && control.ui.directorNumber > selectedNumber) {
        const formControl = this.statementByDirectorsForm.get(control.qname);
        if (formControl && formControl.value) {
          formControl.setValue('', { emitEvent: false });
        }
      }
    });
  }

  /**
   * Update validators for director fields based on visibility
   */
  private updateDirectorFieldValidators(selectedNumber: number): void {
    this.schema.controls.forEach(control => {
      if (control.ui.directorNumber) {
        const formControl = this.statementByDirectorsForm.get(control.qname);
        if (formControl) {
          const validators = [];

          const isVisible = control.ui.directorNumber <= selectedNumber;

          // Director field should be required only if it's visible
          if (isVisible && control.required) {
            validators.push(Validators.required);
          }

          // Schema-declared cap, scoped to visible directors for the same
          // reason `required` is: a hidden field contributes nothing to the
          // verdict, and an error on one is unreachable.
          if (isVisible) {
            const maxLength = getFieldMaxLength(control.qname, this.schema);
            if (maxLength !== null) {
              validators.push(Validators.maxLength(maxLength));
            }
          }

          // For identification number fields, also add pattern validator if type is MyKad/MyPR/MyKAS
          if (control.qname.includes('IdentificationNumber')) {
            const patternValidator = control.validators?.find(validator => validator.name === 'pattern');
            if (patternValidator && (patternValidator as any).pattern) {
              // Check if this field should have pattern validator based on type selection
              if (this.shouldUseNumericFilter(control.qname)) {
                validators.push(Validators.pattern((patternValidator as any).pattern));
              }
            }
          }

          if (validators.length > 0) {
            formControl.setValidators(validators);
          } else {
            formControl.clearValidators();
          }
          formControl.updateValueAndValidity({ emitEvent: false });
        }
      }
    });
  }

  /**
   * Submit form
   */
  submitForm(): void {
    if (this.statementByDirectorsForm.invalid) {
      this.statementByDirectorsForm.markAllAsTouched();
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields',
        life: TOAST_DURATION_LONG_MS
      });
      return;
    }

    const formData = this.statementByDirectorsForm.value;

    this.messageService.add({
      severity: 'success',
      summary: 'Form Submitted',
      detail: 'Statement by directors has been saved successfully',
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Get dropdown options for a field
   */
  getDropdownOptions(enumSource?: string): any[] {
    if (!enumSource) return [];

    const match = enumSource.match(/^type:(.+)$/);
    if (match && match[1]) {
      return this.dropdownOptions[match[1]] || [];
    }

    return [];
  }

  /**
   * Check if a field should be shown based on dynamic conditions
   */
  shouldShowField(control: FormControlSchema): boolean {
    // If this is a director-specific field
    if (control.ui.directorNumber !== undefined) {
      // A cleared count resolves to 0, so NO director block is shown — not even
      // the first one (product owner, 2026-07-28). Nothing stays behind the
      // hidden fields: the watcher blanks them as it hides them (2026-07-29), so
      // a hidden field can never leak a value into state or the payload.
      return control.ui.directorNumber <= this.getSelectedDirectorCount();
    }

    // Fields with requiredIfAllVisibleDirectorsMatch are always visible
    // Their required status is controlled by updateConditionalFieldsVisibility()
    if (control.requiredIfAllVisibleDirectorsMatch) {
      return true;
    }

    // Show all other non-director fields
    return true;
  }

  /**
   * Check if a field is required (considering visibility and conditional requirements)
   */
  /**
   * Does this field's label get a `*`?
   *
   * Single-sourced through `isControlMandatory`, which resolves
   * `requiredIfAllVisibleDirectorsMatch` via the same shared
   * `evaluateRole5RequiredIfAllVisibleDirectorsMatch` this component uses to set
   * its validators — so the marker and the validator can no longer disagree.
   * (It previously read the `conditionalFieldsVisibility` cache, which is still
   * maintained for the validator wiring.)
   *
   * The `isVisible` gate is load-bearing: the schema marks all five director
   * trios `required: true` unconditionally and relies on visibility to scope
   * them, so without the gate every hidden director slot would be marked. This
   * is the shape role 4 should have had — see the note on its `isFieldRequired`.
   */
  isFieldRequired(control: FormControlSchema): boolean {
    return isControlMandatory(control as any, {
      selfData: this.statementByDirectorsForm?.getRawValue() ?? {},
      isVisible: (control) => this.shouldShowField(control as any),
    });
  }

  /**
   * Check if a field has an error
   */
  hasError(fieldName: string): boolean {
    const control = this.statementByDirectorsForm.get(fieldName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  /**
   * Get error message for a field
   */
  getErrorMessage(fieldName: string): string {
    const control = this.statementByDirectorsForm.get(fieldName);
    if (control && control.errors) {
      if (control.errors['required']) {
        return 'This field is required';
      }
      if (control.errors['pattern']) {
        return 'Invalid format';
      }
    }
    return '';
  }

  /**
   * Get form control by qname
   */
  getControl(qname: string): any {
    return this.statementByDirectorsForm.get(qname);
  }

  /**
   * Setup conditional validators for identification number fields
   * These validators apply/remove based on the type of identification selected
   * Uses the dynamically built identificationFieldMappings from schema's conditionalOn property
   */
  private setupConditionalValidators(): void {
    // Use the dynamically built mappings from schema's conditionalOn property
    this.identificationFieldMappings.forEach(({ typeField, numberField, directorNumber }) => {
      const typeControl = this.statementByDirectorsForm.get(typeField);
      const numberControl = this.statementByDirectorsForm.get(numberField);

      if (typeControl && numberControl) {
        typeControl.valueChanges
          .pipe(takeUntil(this.destroy$))
          .subscribe(selectedType => {
            // For director fields (1-5), trigger updateDirectorFieldValidators to handle all director field validators
            if (directorNumber !== null) {
              this.updateDirectorFieldValidators(this.getSelectedDirectorCount());
            } else {
              // For "other person" field, trigger updateConditionalFieldsVisibility()
              // This properly handles the requiredIfAllVisibleDirectorsMatch condition
              // and applies pattern validator based on type selection
              this.updateConditionalFieldsVisibility();
            }
          });
      }
    });
  }

  /**
   * Setup auto-reset watchers for type of identification fields
   * When type changes, clear the identification number field
   * Uses the dynamically built identificationFieldMappings from schema's conditionalOn property
   */
  private setupAutoResetWatchers(): void {
    // Use the dynamically built mappings from schema's conditionalOn property
    this.identificationFieldMappings.forEach(({ typeField, numberField }) => {
      const typeControl = this.statementByDirectorsForm.get(typeField);
      const numberControl = this.statementByDirectorsForm.get(numberField);

      if (typeControl && numberControl) {
        typeControl.valueChanges
          .pipe(takeUntil(this.destroy$))
          .subscribe((selectedType) => {
            // Reset the identification number field when type changes
            numberControl.setValue('', { emitEvent: false });

            // If a type is selected, mark the number field as touched
            // so validation errors display immediately (especially "required" error)
            // This gives immediate feedback that the identification number is required
            if (selectedType) {
              numberControl.markAsTouched();
              numberControl.markAsDirty();
              // Trigger validation update to show errors immediately
              numberControl.updateValueAndValidity({ emitEvent: false });
            } else {
              // Type CLEARED. Re-run the trio's required-state first (clearing
              // the type may itself have emptied the last populated field), then
              // let validity decide what the field looks like: still required
              // and empty => keep the error on screen; nothing to complain
              // about => go back to looking untouched, which was the original
              // 2026-07-29 intent. Blanket-untouching here is what left the
              // "other person" identification number silent while it still
              // blocked submission (P.O. 2026-08-24).
              numberControl.updateValueAndValidity({ emitEvent: false });
              this.updateConditionalFieldsVisibility();
              syncRevealToValidity(numberControl);
            }
          });
      }
    });
  }

  /**
   * Get all error messages for a field (supports simultaneous errors)
   * Implements three-level error fallback:
   * 1. Custom error from JSON schema
   * 2. Label-based error
   * 3. Generic fallback
   */
  getAllErrorMessages(fieldName: string): string[] {
    const control = this.statementByDirectorsForm.get(fieldName);
    const schemaControl = this.schema?.controls?.find(control => control.qname === fieldName);
    const errorMessages: string[] = [];

    if (!control || !(control.dirty || control.touched)) {
      return errorMessages;
    }

    // Handle required error
    if (control.errors?.['required']) {
      // Level 1: Custom error from JSON schema
      if (schemaControl?.error?.required) {
        errorMessages.push(schemaControl.error.required);
      } else if (schemaControl?.label) {
        // Level 2: Label-based error
        errorMessages.push(`${schemaControl.label} is required`);
      } else {
        // Level 3: Generic fallback
        errorMessages.push('This field is required');
      }
    }

    // Handle pattern error
    if (control.errors?.['pattern']) {
      // Level 1: Custom error from JSON schema
      if (schemaControl?.error?.pattern) {
        errorMessages.push(schemaControl.error.pattern);
      } else {
        const validator = schemaControl?.validators?.find(validator => validator.name === 'pattern');
        if (validator && (validator as any).message) {
          errorMessages.push((validator as any).message);
        } else if (schemaControl?.label) {
          errorMessages.push(`${schemaControl.label} has invalid format`);
        } else {
          errorMessages.push('Invalid format');
        }
      }
    }

    // Special case for identification number fields:
    // If field is EMPTY and pattern validator is ACTIVE (MyKad/MyPR/MyKAS selected),
    // show pattern error message even though Angular doesn't report pattern error for empty fields
    if (control.errors?.['required'] && !control.value) {
      const patternValidator = schemaControl?.validators?.find(validator => validator.name === 'pattern');
      if (patternValidator && this.shouldUseNumericFilter(fieldName)) {
        // Pattern validator is active, show its error message too
        if (schemaControl?.error?.pattern) {
          errorMessages.push(schemaControl.error.pattern);
        } else if ((patternValidator as any).message) {
          errorMessages.push((patternValidator as any).message);
        }
      }
    }

    // Handle other validation errors (min, max, etc.) - exclude 'required' and 'pattern'
    if (control.errors) {
      Object.keys(control.errors).forEach(errorKey => {
        if (errorKey !== 'required' && errorKey !== 'pattern') {
          // Try to get custom error from schema
          if (schemaControl?.error?.[errorKey]) {
            errorMessages.push(schemaControl.error[errorKey]);
          } else if (schemaControl?.label) {
            errorMessages.push(`${schemaControl.label} is invalid`);
          } else {
            errorMessages.push(`Validation error: ${errorKey}`);
          }
        }
      });
    }

    return errorMessages;
  }

  /**
   * Setup warning evaluation for all fields with warnings defined in schema
   */
  private setupWarningEvaluation(): void {
    if (!this.schema?.controls) return;

    // Find all controls with warnings defined
    const controlsWithWarnings = this.schema.controls.filter(control => control.warnings && control.warnings.length > 0);

    if (controlsWithWarnings.length === 0) return;

    // Evaluate warnings immediately on load (for existing data)
    setTimeout(() => {
      this.evaluateWarningsManually();
    }, 500);

    // Watch form value changes
    this.statementByDirectorsForm.valueChanges
      .pipe(
        debounceTime(WARNING_EVALUATION_DEBOUNCE_MS),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.evaluateWarningsManually();
      });

    // ----------------------------------
    // Cross-sheet local re-validation watch (golden pattern, role-19).
    //
    // Role-5 reads filingPrerequisites + scopeOfFiling in evaluateWarningsManually
    // (see role0Values @ L863, role2Values @ L866). When either upstream slice
    // changes — same-tab SPA navigation back to this sheet — the warning
    // indicators become stale.
    this.stateService.modifiedState$
      .pipe(
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.statementByDirectors)),
        map(state => JSON.stringify({
          r0: state.filingPrerequisites,
          r1: state.filingInfo,
          r2: state.scopeOfFiling,
        })),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.evaluateWarningsManually();
      });
    // ----------------------------------

    // ----------------------------------
    // Cross-tab safety net — sibling-tab edits to filingPrerequisites or
    // scopeOfFiling must trigger re-evaluation here too.
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.statementByDirectors)),
        filter(() => !!this.schema && !!this.statementByDirectorsForm),
        takeUntil(this.destroy$)
      )
      .subscribe(({ sheetKey }) => {
        if (this.envService.isCrossTabSyncDebugEnabled()) {
          // console.log('[CrossTabSync][remote-validation-triggered]', {
          //   receiver: 'role5-statementByDirectors',
          //   sourceSheetKey: sheetKey,
          // });
        }
        this.evaluateWarningsManually();
      });
    // ----------------------------------

    // ----------------------------------
    // Cross-tab live adoption — when a sibling tab edits THIS sheet's own
    // statementByDirectors slice, `applyRemoteChange` rehydrates the shared
    // state and publishes `changedSheets` on `remoteUpdateApplied$`.
    // `adoptRemoteState()` below repaints this sheet's form from that state
    // (silently, no emitted valueChanges).
    //
    // Deliberately a SEPARATE subscription from the cross-tab safety net
    // above, but that safety net does NOT filter on `changedSheets` — it has
    // no slice filter at all — so it fires on EVERY remote `modifiedState`
    // update, including one that changed THIS sheet's own slice. It is
    // registered first, so on an own-slice remote update it runs
    // `evaluateWarningsManually()` once against the still-STALE,
    // pre-adoption form, then this subscription runs `adoptRemoteState()`.
    // The end state is correct; the intermediate evaluation between the two
    // is wrong-valued for one microtask. Deliberately NOT deduped —
    // deduping risks dropping the upstream-driven refresh the safety net
    // exists for (filingPrerequisites/scopeOfFiling slice changes that
    // never touch `changedSheets` for this sheet at all); see fix-wave I4.
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(({ changedSheets }) => changedSheets.includes(SHEET_KEYS.statementByDirectors)),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.statementByDirectors)),
        filter(() => !!this.schema && !!this.statementByDirectorsForm),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.envService.isCrossTabSyncDebugEnabled()) {
          // console.log('[CrossTabSync][remote-validation-triggered]', {
          //   receiver: 'role5-statementByDirectors',
          // });
        }
        this.adoptRemoteState();
      });
    // ----------------------------------
  }

  /**
   * Cross-tab adoption: a sibling tab edited this sheet, `applyRemoteChange` has
   * already rehydrated the shared state, and this repaints the form from it.
   *
   * MUST NOT write back to state — see the invariant on `adoptIntoAutoSaveSheet`.
   * The validation tail below runs for DISPLAY only; the sibling already
   * persisted its own results.
   *
   * Developer Note (TODO): silent adoption is the P.O. decision of 2026-08-17 and
   * is revisitable. If feedback is later wanted, the two options considered were a
   * brief per-row highlight (preferred — it answers "why did that number change?"
   * at the spot it changed) and a per-adoption toast (cheaper, but says nothing
   * about WHAT changed and stacks on repeated sibling edits).
   */
  private adoptRemoteState(): void {
    adoptIntoAutoSaveSheet(this.statementByDirectorsForm, this.stateService.getSheetData(SHEET_KEYS.statementByDirectors));
    this.evaluateWarningsManually();
  }

  /**
   * Manually trigger warning evaluation for all fields with warnings
   * This can be called when external data changes (e.g., from other sheets)
   *
   * Structures cross-sheet data properly for FieldWarningService:
   * - Cross-sheet data nested under sheet keys (e.g., filingPrerequisites, scopeOfFiling)
   * - Current form values also available at top level for same-sheet validation
   */
  private evaluateWarningsManually(): void {
    if (!this.schema?.controls || !this.statementByDirectorsForm) return;

    // Find all controls with warnings defined
    const controlsWithWarnings = this.schema.controls.filter(control => control.warnings && control.warnings.length > 0);

    if (controlsWithWarnings.length === 0) return;

    // Get all form values for cross-field validations
    const allFormValues = this.statementByDirectorsForm.getRawValue();

    // Get Role-2 (Scope of Filing) values for cross-sheet comparisons
    const role2Values = this.stateService.getSheetData(SHEET_KEYS.scopeOfFiling);

    // Structure cross-sheet data properly for FieldWarningService.
    // The service expects: allValues[sheetKey][fieldQname] for cross-sheet
    // access. buildFilingInfoBagEntry() supplies the active filing-info slot
    // under both alias keys (filingPrerequisites + filingInfo) so phase-aware
    // resolution stays lossless across Phase 1 and Phase 2.
    // Also include flat values for same-sheet field access.
    const allValuesForWarnings = {
      ...allFormValues,
      ...this.stateService.buildFilingInfoBagEntry(),
      scopeOfFiling: role2Values || {}
    };

    controlsWithWarnings.forEach(control => {
      const formControl = this.statementByDirectorsForm.get(control.qname) as FormControl;
      if (formControl && control.warnings) {
        const warnings = this.warningService.evaluateWarnings(
          formControl,
          control.warnings,
          allValuesForWarnings,
          true // isEditedMode - set to true to enable warning evaluation
        );
        this.fieldWarnings[control.qname] = warnings;
      }
    });

  }

  /**
   * Get warnings for a specific field
   */
  getFieldWarnings(fieldName: string): WarningResult[] {
    return this.fieldWarnings[fieldName] || [];
  }

  /**
   * Check if a field has warnings
   */
  hasWarnings(fieldName: string): boolean {
    return this.getFieldWarnings(fieldName).length > 0;
  }

  /**
   * Get all warnings for a specific field (for app-warning-message component)
   * Returns array of warning objects with message and severity properties for display
   * Sorted: errors first, then warnings
   */
  getAllWarnings(qname: string): WarningMessage[] {
    const warnings: WarningMessage[] = [];
    const fieldWarnings = this.fieldWarnings[qname] || [];
    fieldWarnings.forEach(warning => {
      // Map severity to 'warning' | 'error' (treat 'info' as 'warning')
      const severity: 'warning' | 'error' = warning.severity === 'error' ? 'error' : 'warning';
      warnings.push({ message: warning.message, severity });
    });

    // Sort: errors first, then warnings
    return warnings.sort((firstWarning, secondWarning) => {
      if (firstWarning.severity === 'error' && secondWarning.severity !== 'error') return -1;
      if (firstWarning.severity !== 'error' && secondWarning.severity === 'error') return 1;
      return 0;
    });
  }

  /**
   * Split warning message to identify asterisk for styling
   * Returns array of message parts with isAsterisk flag
   */
  splitWarningMessage(message: string): Array<{ text: string; isAsterisk: boolean }> {
    const parts: Array<{ text: string; isAsterisk: boolean }> = [];
    const asteriskPattern = /\*/g;
    let lastIndex = 0;
    let match;

    while ((match = asteriskPattern.exec(message)) !== null) {
      // Add text before asterisk
      if (match.index > lastIndex) {
        parts.push({ text: message.substring(lastIndex, match.index), isAsterisk: false });
      }
      // Add asterisk
      parts.push({ text: '*', isAsterisk: true });
      lastIndex = match.index + 1;
    }

    // Add remaining text
    if (lastIndex < message.length) {
      parts.push({ text: message.substring(lastIndex), isAsterisk: false });
    }

    return parts;
  }

  /**
   * Check if identification number field should use numeric-only input
   * Only applies when type is MyKad, MyPR, or MyKAS
   * Uses the dynamically built identificationFieldMappings from schema's conditionalOn property
   */
  shouldUseNumericFilter(numberFieldQname: string): boolean {
    // Find the mapping for this identification number field from the dynamically built mappings
    const mapping = this.identificationFieldMappings.find(mapping => mapping.numberField === numberFieldQname);
    if (!mapping) return false;

    const typeValue = this.statementByDirectorsForm.get(mapping.typeField)?.value;
    // Use case-insensitive comparison against the values from schema's conditionalOn
    return role5TypeRequiresPattern(typeValue, mapping.values);
  }

  /**
   * Type 2: Check if field should be hidden (not rendered)
   * Returns true if field should be completely hidden based on schema's hideWhen configuration
   *
   * For inherited fields: Also checks if field exists in Role-1 (filingInfo)
   */
  shouldHideField(fieldQname: string): boolean {
    const control = this.schema.controls.find((control) => control.qname === fieldQname);
    if (!control) {
      return false;
    }

    // Read from the active filing-info source for the current phase
    // (filingPrerequisites in Phase 1, filingInfo in Phase 2). See PR-F.
    const filingInfoData = this.stateService.getActiveFilingInfoData();

    // For inherited fields: Hide if field doesn't exist or is empty upstream
    if (control.ui.inheritFrom === 'filingInfo') {
      const role1Value = filingInfoData?.[fieldQname];

      // Hide if field is not present or is empty/null/undefined in Role-1
      if (
        role1Value === undefined ||
        role1Value === null ||
        role1Value === ''
      ) {
        return true;
      }
    }

    // Schema-driven hideWhen logic
    if (control.ui.hideWhen) {
      const { field, values } = control.ui.hideWhen;
      const triggerValue = filingInfoData?.[field];

      if (values.includes(triggerValue)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if a field has a meaningful value (not null, undefined, or empty string)
   */
  private fieldHasValue(control: FormControlSchema, allFormData: any): boolean {
    // Handle dimensional fields
    if (control.ContextPolicy?.allowedAxes && control.ContextPolicy.allowedAxes.length > 0) {
      return control.ContextPolicy.allowedAxes.some(axis => {
        const fieldName = `${control.qname}[${axis}]`;
        const value = allFormData[fieldName];
        return value !== null && value !== undefined && value !== '';
      });
    }

    // Handle regular fields
    const value = allFormData[control.qname];
    return value !== null && value !== undefined && value !== '';
  }

  /**
   * Get all visible fields for submission
   * Returns all visible fields based on schema visibility rules
   * Filters out unused fields (CLBG, scope-specific, etc.) based on submission type
   * CRITICAL: Transforms frontend bracket notation to backend nested format
   */
  getVisibleFormData(): any {
    const allFormData = this.statementByDirectorsForm.getRawValue();
    const submissionData: any = {};

    // Dynamically collect all visible fields based on schema
    this.schema.controls.forEach((control: FormControlSchema) => {
      // Skip fields that should be hidden (based on hideWhen rules or inherited fields not in Role-0)
      if (this.shouldHideField(control.qname)) {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`⏭️ Skipping hidden field: ${control.qname}`);
        return;
      }

      // Check conditional visibility (requiredIf)
      if (control.requiredIf) {
        const dependentControl = this.statementByDirectorsForm.get(control.requiredIf.qname);
        if (dependentControl?.value !== control.requiredIf.equals) {
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`⏭️ Skipping conditionally hidden field: ${control.qname}`);
          return;
        }
      }

      // Check conditional visibility (requiredIfAny)
      if (control.requiredIfAny) {
        const shouldShow = control.requiredIfAny.some((condition) => {
          const dependentControl = this.statementByDirectorsForm.get(condition.qname);
          return dependentControl?.value === condition.equals;
        });
        if (!shouldShow) {
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`⏭️ Skipping conditionally hidden field: ${control.qname}`);
          return;
        }
      }

      // Skip fields with empty values (optional optimization)
      // This reduces payload size by not sending empty optional fields
      const hasValue = this.fieldHasValue(control, allFormData);
      if (!control.required && !hasValue) {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`⏭️ Skipping empty optional field: ${control.qname}`);
        return;
      }

      // Handle fields with dimensional axes (e.g., MSICCode with business members)
      if (control.ContextPolicy?.allowedAxes && control.ContextPolicy.allowedAxes.length > 0) {
        control.ContextPolicy.allowedAxes.forEach(axis => {
          const fieldName = `${control.qname}[${axis}]`;
          if (allFormData.hasOwnProperty(fieldName)) {
            submissionData[fieldName] = allFormData[fieldName];
            // Debug Log (Only enable when required to debug, do not allow this to land into prod)
            // console.log(`✅ Including dimensional field: ${fieldName} = ${allFormData[fieldName]}`);
          }
        });
      } else {
        // Regular field without axes
        const fieldName = control.qname;
        if (allFormData.hasOwnProperty(fieldName)) {
          submissionData[fieldName] = allFormData[fieldName];
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`✅ Including field: ${fieldName}`);
        }
      }
    });

    return submissionData;
  }

  onInputChange(event: InputEvent) {
    handleDateInputChange(event, this.statementByDirectorsForm);
  }

  /**
   * Get max length for a field.
   *
   * For an *IdentificationNumber control the cap is conditional on the paired
   * Type of Identification: 12 for the numeric MyKad/MyPR/MyKAS IDs, 255 free
   * text otherwise. The cap mirrors {@link shouldBeNumericOnly} so the rendered
   * `maxlength`, the keystroke filter and the conditional pattern validator can
   * never disagree. Every other field (incl. text blocks) falls back to its
   * schema-derived max length.
   */
  getMaxLength(fieldName: string): number | null {
    const mapping = this.identificationFieldMappings.find(mapping => mapping.numberField === fieldName);
    if (!mapping) {
      return getFieldMaxLength(fieldName, this.schema);
    }
    return this.shouldBeNumericOnly(fieldName)
      ? NUMERIC_IDENTIFICATION_MAX_LENGTH
      : FREE_TEXT_IDENTIFICATION_MAX_LENGTH;
  }

  /**
   * Determine if an identification number field should be numeric-only
   * (true when the paired Type of Identification is MyKad/MyPR/MyKAS).
   *
   * Resolves the paired type field from the schema-derived
   * `identificationFieldMappings` (each pattern validator's `conditionalOn`),
   * NOT by string derivation: role 5 has irregular qnames the heuristic cannot
   * match (the 2nd director's type carries an extra "Number"; the "other
   * person" type/number pair diverge on "Statements" vs "Management"). Using the
   * same authoritative source as the conditional pattern validator keeps the
   * filter, cap and validator in agreement.
   */
  shouldBeNumericOnly(fieldName: string): boolean {
    return this.shouldUseNumericFilter(fieldName);
  }

  onCancelConfirmed(): void {
    this.loadSavedState();
    this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
    this.statementByDirectorsForm.markAsPristine();
  }
}
